/**
 * What the message editor should be showing, given the template and the list.
 *
 * The one decision worth stating here: **the counter measures the message, not
 * the template.** `%promo%` is seven characters that are never sent, and the
 * code that replaces it varies — so a count of the template as typed is a
 * number that is wrong for every recipient. What this measures is the longest
 * message the current list can produce, which is the worst case and therefore
 * the only count that cannot surprise anyone with a bill (PLAN §8).
 *
 * `longestMessage` and `measureSms` are the same functions the server runs
 * before anything is sent, so the segment count on screen is provably the
 * count that gets billed.
 *
 * Pure, and testable without a DOM, for the same reasons `blocks-view.ts` and
 * `import-view.ts` are.
 */

import { templateProblems, type CampaignProblem } from '../domain/campaign-check.ts';
import { measureSms, type SmsEncoding } from '../domain/sms.ts';
import { checkTemplate, longestMessage } from '../domain/template.ts';

/**
 * `template-multi-segment` is this module's own: it is a warning rather than a
 * refusal, so it has no business in the verdict `campaign-check` reaches. The
 * two refusals come from there.
 */
export type TemplateFinding = CampaignProblem | {
  readonly name: 'template-multi-segment';
  readonly params: Readonly<Record<string, number>>;
};

export interface TemplateView {
  /** As the provider counts it: an escape pair is two, an emoji is two. */
  readonly length: number;
  readonly parts: number;
  /** Total room at this many parts, so the two numbers explain each other. */
  readonly capacity: number;
  readonly encoding: SmsEncoding;
  /** Characters left before another part is needed. */
  readonly remaining: number;
  /** Whether anything has been typed. The counters stay away until it has. */
  readonly typed: boolean;
  /**
   * Past `WARN_IF_PARTS_ABOVE`. The counters go amber on it, and the headroom
   * gives way to the warning — at that point how much room is left before the
   * *next* part is not the question anyone is asking.
   */
  readonly overThreshold: boolean;
  readonly findings: readonly TemplateFinding[];
  /** Whether this template could be sent at all. A warning never clears this. */
  readonly ready: boolean;
}

export interface TemplateViewOptions {
  /** `WARN_IF_PARTS_ABOVE`, from configuration. One segment by default. */
  readonly warnIfPartsAbove?: number;
}

export function templateView(
  template: string,
  codes: readonly string[],
  options: TemplateViewOptions = {},
): TemplateView {
  const threshold = options.warnIfPartsAbove ?? 1;
  // The worst case the current list can produce. With no list yet this is the
  // template with an empty substitution — the honest floor, and the same
  // fallback `longestMessage` gives the confirmation screen.
  const worst = longestMessage(template, codes);
  const metrics = measureSms(worst.message);
  const typed = template !== '';

  // The refusals are `campaign-check`'s verdict, reached identically on the
  // server; this module only adds the warning and the numbers.
  const findings: TemplateFinding[] = [...templateProblems(template, worst)];
  const refusals = findings.length;

  // Multi-segment messages are the easiest way to double a campaign's cost by
  // accident, and because promo codes vary in length a template can be one
  // segment for most recipients and two for a handful. Said out loud, and only
  // said — §59 is explicit that the administrator sees the truth and decides.
  const overThreshold = metrics.parts > threshold;
  if (overThreshold) {
    findings.push({ name: 'template-multi-segment', params: { parts: metrics.parts } });
  }

  return {
    length: metrics.length,
    parts: metrics.parts,
    capacity: metrics.capacity,
    encoding: metrics.encoding,
    remaining: metrics.remaining,
    typed,
    overThreshold,
    findings,
    // The two refusals, and an empty template — which is one too, just not one
    // worth saying out loud yet. The segment warning is deliberately not here.
    ready: checkTemplate(template).ok && refusals === 0,
  };
}
